import type { ARQrParams, EarVariant } from './types';
import {
  VALID_MODELS,
  BODY_ALLOW,
  FACE_TEXTURE_COUNT,
  EAR_VARIANTS,
  EAR_VARIANT_MODELS,
} from '../character/constants';

const SCHEME_PREFIX = 'msqr:v2:';

export type ParseFailure =
  | 'scheme'         // not msqr: at all
  | 'version_old'    // older version
  | 'version_new'    // future version
  | 'base64'
  | 'json'
  | 'app'
  | 'invalid_field';

export type ParseResult =
  | { ok: true; params: ARQrParams }
  | { ok: false; reason: ParseFailure };

function decodeBase64Url(b64: string): string {
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (padded.length % 4)) % 4;
  return atob(padded + '='.repeat(padLen));
}

export function parseARQr(raw: string): ParseResult {
  // URL 形式 (https://.../#msqr:v2:...) なら # 以降を取り出す
  let target = raw.trim();
  if (/^https?:\/\//i.test(target)) {
    const hashIdx = target.indexOf('#');
    if (hashIdx < 0) return { ok: false, reason: 'scheme' };
    target = target.slice(hashIdx + 1);
  }

  if (!target.startsWith('msqr:')) return { ok: false, reason: 'scheme' };
  if (target.startsWith('msqr:v1:')) return { ok: false, reason: 'version_old' };
  if (!target.startsWith(SCHEME_PREFIX)) {
    if (target.startsWith('msqr:v')) return { ok: false, reason: 'version_new' };
    return { ok: false, reason: 'scheme' };
  }
  const b64 = target.slice(SCHEME_PREFIX.length);

  let json: string;
  try {
    json = decodeBase64Url(b64);
  } catch {
    return { ok: false, reason: 'base64' };
  }

  let obj: unknown;
  try {
    obj = JSON.parse(json);
  } catch {
    return { ok: false, reason: 'json' };
  }

  if (!isObject(obj)) return { ok: false, reason: 'json' };
  if (obj['v'] === 1) return { ok: false, reason: 'version_old' };
  if (obj['v'] !== 2) return { ok: false, reason: 'version_new' };
  if (obj['app'] !== 'MS') return { ok: false, reason: 'app' };

  const m = obj['m'];
  const b = obj['b'];
  const f = obj['f'];
  const h = obj['h'];
  const w = obj['w'];
  const e = obj['e'] ?? ''; // 省略可、デフォルト '' (耳なし)

  if (typeof m !== 'number' || !VALID_MODELS.has(m)) {
    console.warn('[parseARQr] invalid m:', m);
    return { ok: false, reason: 'invalid_field' };
  }
  if (typeof b !== 'string' || !BODY_ALLOW.has(b)) {
    console.warn('[parseARQr] invalid b:', b);
    return { ok: false, reason: 'invalid_field' };
  }
  if (!Number.isInteger(f) || (f as number) < 0 || (f as number) >= FACE_TEXTURE_COUNT) {
    console.warn('[parseARQr] invalid f:', f);
    return { ok: false, reason: 'invalid_field' };
  }
  if (!Number.isInteger(h) || (h as number) < 1 || (h as number) > 5) {
    console.warn('[parseARQr] invalid h:', h);
    return { ok: false, reason: 'invalid_field' };
  }
  if (!Number.isInteger(w) || (w as number) < 1 || (w as number) > 5) {
    console.warn('[parseARQr] invalid w:', w);
    return { ok: false, reason: 'invalid_field' };
  }
  if (typeof e !== 'string' || !EAR_VARIANTS.has(e)) {
    console.warn('[parseARQr] invalid e:', e);
    return { ok: false, reason: 'invalid_field' };
  }
  // humanoid (11/12/13) では耳付き不可。'' に正規化
  const normalizedE: EarVariant = EAR_VARIANT_MODELS.has(m) ? (e as EarVariant) : '';

  const result: ARQrParams = {
    m,
    b,
    f: f as number,
    h: h as number,
    w: w as number,
  };
  if (normalizedE) result.e = normalizedE;
  return { ok: true, params: result };
}

export function entryKey(p: ARQrParams): string {
  return `m${p.m}_b${p.b}_f${p.f}_h${p.h}_w${p.w}_e${p.e ?? ''}`;
}

export function failureMessage(reason: ParseFailure): string {
  switch (reason) {
    case 'scheme':
      return 'この QR は MarbleScramble AR 用ではありません';
    case 'version_old':
      return 'この QR は古い形式です（本体ゲームを更新してください）';
    case 'version_new':
      return 'この QR は新しい形式です（このアプリを更新してください）';
    case 'base64':
      return 'QR データが破損しています (base64)';
    case 'json':
      return 'QR データが破損しています (JSON)';
    case 'app':
      return 'この QR は別のアプリ用です';
    case 'invalid_field':
      return 'QR の値が不正です';
  }
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
